/*
 * Main App Component
 *
 * Changes:
 * - Added id="gallery" to gallery section for navigation scroll
 */

import Layout from './components/Layout';
import ProjectGrid from './components/ProjectGrid';
import Testimonials from './components/Testimonials';
import Gallery from './components/Gallery';
import { Target, Award, ArrowRight, Mail, MapPin, Phone, ChevronDown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Hero from './components/Hero';
import NewsUpdates from './components/NewsUpdates';
import ScrollToTop from './components/ScrollToTop';
import { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';
import { Tour } from './types/tours';

const App = () => {
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [contactFormSubmitted, setContactFormSubmitted] = useState(false);
  const [contactFormLoading, setContactFormLoading] = useState(false);
  const [contactFormError, setContactFormError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const { data, error } = await supabase
          .from('tours')
          .select('id, name, slug')
          .eq('status', 'published')
          .order('display_order', { ascending: true })
          .order('created_at', { ascending: false });

        if (error) throw error;
        setTours(data || []);
      } catch (err) {
        console.error('Error fetching tours:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTours();
  }, []);

  const handleTourSelect = (slug: string) => {
    // Don't redirect, just update the form value
    return;
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactFormLoading(true);
    setContactFormError(null);
    
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const { sendContactEmail } = await import('./lib/emailjs');
      
      await sendContactEmail({
        firstName: formData.get('firstName') as string,
        lastName: formData.get('lastName') as string,
        email: formData.get('email') as string,
        tour: formData.get('tour') as string,
        message: formData.get('message') as string
      });
      
      setContactFormSubmitted(true);
      form.reset();
      // Reset success message after 5 seconds
      setTimeout(() => {
        setContactFormSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setContactFormError('There was an error submitting your message. Please try again.');
    } finally {
      setContactFormLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <Hero />

      {/* Latest Projects */}
      <section id="adventures" className="relative py-16 bg-[#2A382A]">
        <div className="container max-w-7xl mx-auto px-4 md:px-8 lg:px-16 mb-16">
          <h1 className="font-barrio text-5xl md:text-6xl tracking-tighter [text-shadow:_0_-4px_8px_rgba(0,0,0,0.4)] mb-4">
            OUR EXCITING ADVENTURES.
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-2xl">
            Join us on an unforgettable journey to discover the most vibrant frogs and magnificent birds in our pristine Costa Rican rainforests.
          </p>
        </div>
        <div className="container max-w-7xl mx-auto">
          <ProjectGrid />
        </div>
      </section>

      {/* Gallery */}
      <section id="gallery" className="relative py-16 bg-[#2A382A]">
        <div className="container max-w-7xl mx-auto px-4 md:px-8 lg:px-16 mb-8">
          <h1 className="font-barrio text-5xl md:text-6xl tracking-tighter [text-shadow:_0_-4px_8px_rgba(0,0,0,0.4)] mb-4">
            GALLERY.
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-2xl">
            Explore our stunning collection of wildlife photographs captured during our tours.
          </p>
        </div>
        <div className="container max-w-7xl mx-auto">
          <Gallery />
        </div>
      </section>

      {/* Testimonials */}
      <section className="container max-w-7xl mx-auto px-4 py-16 md:px-8 lg:px-16">
        <h2 className="font-barrio text-4xl md:text-5xl mb-4">WHAT DO OUR ADVENTURERS SAY?</h2>
        <Testimonials />
        
        {/* About Us Section */}
        <div className="mt-24 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="relative aspect-[16/9] rounded-2xl overflow-hidden">
            <img 
              src="/assets/about-us.webp"
              alt="About Sierpe Frogs"
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="space-y-6">
            <div>
              <h2 className="font-barrio text-4xl md:text-5xl mb-4">WHO WE ARE?</h2>
              <p className="text-white/80 text-lg">
                We are a passionate team of naturalist guides and wildlife photographers dedicated to sharing 
                the incredible biodiversity of Costa Rica's rainforests while contributing to research and conservation efforts.
              </p>
              <Link
                to="/about-us"
                className="flex items-center justify-between px-4 py-3 sm:py-2 bg-white/20 backdrop-blur-sm text-white rounded-[1rem] hover:bg-white/30 transition-all duration-300 hover:scale-105 w-full sm:w-auto mt-4"
              >
                Read More About Us
                <span className="bg-[#953B0E] p-2 rounded-lg ml-6 border-2 border-[#E85C0D]">
                  <ArrowRight className="w-5 h-5" />
                </span>
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-[#E85C0D]" />
              </div>
              <h3 className="text-xl font-semibold">Our Mission</h3>
            </div>
            <p className="text-white/70">
              To provide unparalleled ecotours while supporting scientific research and inspiring 
              conservation through wildlife photography.
            </p>
          </div>
          
          <div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-[#E85C0D]" />
              </div>
              <h3 className="text-xl font-semibold">Passionate and Certified</h3>
            </div>
            <p className="text-white/70">
              Our expert guides combine deep knowledge with a genuine passion for Costa Rica's wildlife.
            </p>
          </div>
        </div>
      </section>

      {/* News & Updates */}
      <NewsUpdates />

      {/* Contact Us */}
      <section id="contact" className="bg-[#2A382A] py-16">
        <div className="container max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
          <div className="text-center mb-16">
            <h2 className="font-barrio text-4xl md:text-5xl mb-4">Get in Touch</h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              Have questions about our tours or want to make a booking? We're here to help! 
              Fill out the form below and we'll get back to you as soon as possible.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-[#1F2B1F] p-6 rounded-xl">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#E85C0D]" />
                  </div>
                  <h3 className="text-lg font-semibold">Visit Us</h3>
                </div>
                <a 
                  href="https://maps.app.goo.gl/RCJkAF6xdiVyPVNn8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white transition-colors underline decoration-white/70 hover:decoration-white"
                >
                  Blvd. Villalobos, Provincia de Puntarenas, Sierpe, 60503, Costa Rica
                </a>
              </div>

              <div className="bg-[#1F2B1F] p-6 rounded-xl">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-[#E85C0D]" />
                  </div>
                  <h3 className="text-lg font-semibold">Call Us</h3>
                </div>
                <a 
                  href="tel:+50689190917" 
                  className="text-white/70 hover:text-white transition-colors underline decoration-white/70 hover:decoration-white"
                >
                  +506 8919 0917
                </a>
                <p className="text-sm text-white/70 mt-1">You can always try to give us a call</p>
              </div>

              <div className="bg-[#1F2B1F] p-6 rounded-xl">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                    <Mail className="w-6 h-6 text-[#E85C0D]" />
                  </div>
                  <h3 className="text-lg font-semibold">Email Us</h3>
                </div>
                <a 
                  href="mailto:sierpefrogs@gmail.com" 
                  className="text-white/70 hover:text-white transition-colors underline decoration-white/70 hover:decoration-white"
                >
                  sierpefrogs@gmail.com
                </a>
                <p className="text-sm text-white/70 mt-1">We'll try to respond within 24 hours</p>
              </div>
            </div>

            {/* Contact Form */}
            <form 
              onSubmit={handleContactSubmit}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-white/70 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    className="w-full px-4 py-3 bg-[#1F2B1F] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-white/70 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    className="w-full px-4 py-3 bg-[#1F2B1F] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white"
                  />
                </div>
              </div>

              <div className="mt-2">
                <label htmlFor="email" className="block text-sm font-medium text-white/70 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 bg-[#1F2B1F] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white"
                />
              </div>

              <div className="mt-2">
                <label htmlFor="tour" className="block text-sm font-medium text-white/70 mb-2">
                  Select Tour
                  <span className="text-white/70 ml-2">(Optional)</span>
                  {loading && <span className="ml-2 text-xs">(Loading...)</span>}
                </label>
                <div className="relative">
                  <select
                    id="tour"
                    name="tour"
                    className="w-full px-4 py-3 bg-[#1F2B1F] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white appearance-none pr-10"
                    defaultValue=""
                  >
                    <option value="">Select a tour (optional)</option>
                    {tours.map((tour) => (
                      <option key={tour.id} value={tour.slug}>
                        {tour.name}
                      </option>
                    ))}
                    <option value="custom">Custom Tour</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 pointer-events-none" />
                </div>
              </div>

              <div className="mt-2">
                <label htmlFor="message" className="block text-sm font-medium text-white/70 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  required
                  className="w-full px-4 py-3 bg-[#1F2B1F] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white resize-none"
                ></textarea>
              </div>

              {contactFormSubmitted && (
                <div className="bg-green-500/10 text-green-500 p-4 rounded-lg">
                  Thanks for your submission! We'll try to respond within 24 hours.
                </div>
              )}

              {contactFormError && (
                <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
                  {contactFormError}
                </div>
              )}

              <button
                type="submit"
                disabled={contactFormLoading}
                className="w-full px-8 py-4 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors font-medium border-2 border-[#E85C0D] mt-2"
              >
                {contactFormLoading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      <ScrollToTop />
    </Layout>
  );
};

export default App;